// ExportButton Component
// Provides export functionality for diagrams and reports
// ============================================================

import React, { useState } from 'react';
import { Download, FileImage, FileText, Loader2, ChevronDown } from 'lucide-react';
import clsx from 'clsx';
import { exportAsImage, exportAsPDF } from '../../utils/exportUtils';

interface ExportButtonProps {
    elementId: string;
    fileName: string;
    formats?: ('png' | 'pdf')[];
    title?: string;
    className?: string;
    variant?: 'primary' | 'secondary' | 'ghost';
    size?: 'sm' | 'md' | 'lg';
}

export const ExportButton: React.FC<ExportButtonProps> = ({
    elementId,
    fileName,
    formats = ['png', 'pdf'],
    title,
    className,
    variant = 'secondary',
    size = 'md',
}) => {
    const [isExporting, setIsExporting] = useState(false);
    const [showMenu, setShowMenu] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleExport = async (format: 'png' | 'pdf') => {
        setIsExporting(true);
        setError(null);
        setShowMenu(false);

        try {
            if (format === 'png') {
                await exportAsImage(elementId, fileName);
            } else {
                await exportAsPDF(elementId, fileName, { title });
            }
        } catch (err) {
            setError('Export failed. Please try again.');
            console.error('Export error:', err);
        } finally {
            setIsExporting(false);
        }
    };

    const sizeClasses = {
        sm: 'px-3 py-1.5 text-sm',
        md: 'px-4 py-2',
        lg: 'px-5 py-2.5 text-lg',
    };

    const variantClasses = {
        primary: 'bg-blue-600 text-white hover:bg-blue-700 border-blue-600',
        secondary: 'bg-white text-gray-700 hover:bg-gray-50 border-gray-300',
        ghost: 'bg-transparent text-gray-600 hover:bg-gray-100 border-transparent',
    };

    // Single format - simple button
    if (formats.length === 1) {
        return (
            <button
                onClick={() => handleExport(formats[0])}
                disabled={isExporting}
                className={clsx(
                    'inline-flex items-center gap-2 rounded-lg border font-medium transition-all',
                    sizeClasses[size],
                    variantClasses[variant],
                    isExporting && 'opacity-50 cursor-not-allowed',
                    className
                )}
            >
                {isExporting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                    <Download className="w-4 h-4" />
                )}
                Export as {formats[0].toUpperCase()}
            </button>
        );
    }

    // Multiple formats - dropdown
    return (
        <div className="relative">
            <button
                onClick={() => setShowMenu(!showMenu)}
                disabled={isExporting}
                className={clsx(
                    'inline-flex items-center gap-2 rounded-lg border font-medium transition-all',
                    sizeClasses[size],
                    variantClasses[variant],
                    isExporting && 'opacity-50 cursor-not-allowed',
                    className
                )}
            >
                {isExporting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                    <Download className="w-4 h-4" />
                )}
                Export
                <ChevronDown className={clsx('w-4 h-4 transition-transform', showMenu && 'rotate-180')} />
            </button>

            {showMenu && (
                <>
                    {/* Backdrop */}
                    <div
                        className="fixed inset-0 z-10"
                        onClick={() => setShowMenu(false)}
                    />

                    {/* Dropdown Menu */}
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-20 overflow-hidden">
                        {formats.includes('png') && (
                            <button
                                onClick={() => handleExport('png')}
                                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                            >
                                <FileImage className="w-5 h-5 text-green-600" />
                                <div>
                                    <p className="font-medium text-gray-900">Export as PNG</p>
                                    <p className="text-xs text-gray-500">High-quality image</p>
                                </div>
                            </button>
                        )}
                        {formats.includes('pdf') && (
                            <button
                                onClick={() => handleExport('pdf')}
                                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 transition-colors border-t border-gray-100"
                            >
                                <FileText className="w-5 h-5 text-red-600" />
                                <div>
                                    <p className="font-medium text-gray-900">Export as PDF</p>
                                    <p className="text-xs text-gray-500">Print-ready document</p>
                                </div>
                            </button>
                        )}
                    </div>
                </>
            )}

            {error && (
                <p className="absolute top-full mt-2 text-sm text-red-600">{error}</p>
            )}
        </div>
    );
};

export default ExportButton;
